/**
 * Pure enforcement policy for the KyberSwap execution path, extracted out of
 * useUniversalRouter.ts so the safety decisions have a single implementation
 * that both the production hook and the test suite exercise directly (no
 * hand-rolled parallel copy in tests that can drift from what actually runs).
 *
 * Also the single home for the fallback fee constants used when a worker
 * response doesn't include `feeMeta` (older/un-redeployed worker) - these
 * are consumed both here (execution/enforcement) and from
 * state/routing/utils.ts (fee display), so the number shown to the user and
 * the number enforced at build time can never diverge.
 */
import { ChargeFeeBy, ResolvedKyberFeeMeta, resolveKyberFeeMeta } from 'utils/kyberFeeMeta'
import { kyberMinimumAmountOut } from 'utils/kyberMinimumOut'

// Must match worker.js's FEE_BPS/FEE_RECEIVER when kept in sync manually;
// once every deployed worker sends feeMeta this fallback becomes dead code.
export const KYBER_FEE_BPS_FALLBACK = 100n
// worker.js's fetchKyberRoute() requests routeSummary with chargeFeeBy set
// (currency_in for native input, currency_out for ERC-20 input) - in both
// modes KyberSwap deducts the fee before returning amountOut, so the quote
// is already NET of the app fee regardless of mode. This is only a fallback
// for when the worker doesn't send feeMeta.quoteIsNetOfFee.
export const KYBER_QUOTE_IS_NET_OF_FEE_FALLBACK = true
// worker.js's FEE_RECEIVER constant - used for display purposes only when
// feeMeta is absent (execution never trusts a client-side recipient; the
// worker encodes the real recipient into the build calldata itself).
export const KYBER_FEE_RECIPIENT_FALLBACK = '0x4e5765072Fd9eEcDc49A8c9adB81707D106e3F65'

// worker.js's kyberFeeMeta(nativeInput) (worker.js:47-58) derives chargeFeeBy
// per-request from whether the swap's input is the chain's native asset:
// `nativeInput -> 'currency_in'`, otherwise -> 'currency_out'. When feeMeta
// is entirely absent (older/un-redeployed worker) this fallback must mirror
// that same per-trade selection - a single static mode is wrong for whichever
// side of the branch it doesn't match, so the caller must pass the trade's
// own native-input flag rather than getting a one-size-fits-all constant.
export function kyberChargeFeeByFallback(isNativeInput: boolean): ChargeFeeBy {
  return isNativeInput ? 'currency_in' : 'currency_out'
}

export function getKyberFeeFallback(isNativeInput: boolean): ResolvedKyberFeeMeta {
  return {
    feeBps: KYBER_FEE_BPS_FALLBACK,
    quoteIsNetOfFee: KYBER_QUOTE_IS_NET_OF_FEE_FALLBACK,
    chargeFeeBy: kyberChargeFeeByFallback(isNativeInput),
  }
}

export interface AssertKyberBuildSafeParams {
  /** amountOut from the ORIGINAL quote shown to the user (raw integer string parsed to bigint upstream). */
  quotedAmountOut: bigint
  /** amountOut from the fresh build fetched at swap time. */
  buildAmountOut: bigint
  /** Raw (unparsed) feeMeta attached to the original quote, if any - parsed internally via resolveKyberFeeMeta. */
  quoteFeeMetaRaw?: unknown
  /** Raw (unparsed) feeMeta attached to the fresh build, if any - parsed internally via resolveKyberFeeMeta. */
  buildFeeMetaRaw?: unknown
  /** User's slippage tolerance in bps, unmodified by the app fee (see kyberMinimumAmountOut). */
  slippageBps: bigint
  /**
   * Whether the trade's input currency is the chain's native asset. Used
   * only to pick the correct fallback chargeFeeBy (see
   * getKyberFeeFallback/kyberChargeFeeByFallback above) when BOTH quote-time
   * and build-time feeMeta are absent - mirrors worker.js's per-request
   * chargeFeeBy selection exactly, so an un-redeployed worker doesn't cause
   * enforcement to silently assume the wrong mode.
   */
  isNativeInput: boolean
}

/**
 * The single enforcement policy for a KyberSwap execution:
 *  1. Resolve quote-time vs build-time feeMeta (prefer quote, then build,
 *     then the hardcoded fallback above). Reject present-but-invalid
 *     metadata with `[FEE_META_INVALID]`; only an absent field from a legacy
 *     worker may use the fallback. Reject with
 *     `[FEE_META_DISAGREEMENT]` if both are present, valid, and disagree.
 *     If quote-time feeMeta is absent (e.g. an old/cached quote from a
 *     rolling worker deployment) but build-time feeMeta is present, the
 *     expected quote-time policy is synthesized from the fallback contract
 *     (see resolveKyberFeeMeta) and the build is checked against that
 *     instead of being trusted unconditionally.
 *  2. Compute the enforceable minimum output from the ORIGINAL quote (fee
 *     modeled as a fee, then slippage - see kyberMinimumAmountOut) and
 *     reject with `[MINIMUM_OUT_BELOW_FLOOR]` if the fresh build's amountOut
 *     has fallen below that floor.
 *
 * Throws on rejection; returns void on success. This is a pure function -
 * no network calls, no React - so it can be exercised directly in tests
 * against the exact logic the production swap path runs.
 */
export function assertKyberBuildSafe(params: AssertKyberBuildSafeParams): void {
  const { quotedAmountOut, buildAmountOut, quoteFeeMetaRaw, buildFeeMetaRaw, slippageBps, isNativeInput } = params

  const feeResolution = resolveKyberFeeMeta(
    quoteFeeMetaRaw,
    buildFeeMetaRaw,
    getKyberFeeFallback(isNativeInput),
    KYBER_FEE_RECIPIENT_FALLBACK,
  )
  if (feeResolution.invalid) {
    const invalidSources = [
      feeResolution.invalid.quote ? 'quote' : undefined,
      feeResolution.invalid.build ? 'build' : undefined,
    ]
      .filter(Boolean)
      .join(' and ')
    throw new Error(
      `KyberSwap safety check failed: ${invalidSources} feeMeta was present but invalid [FEE_META_INVALID]. Transaction blocked to protect funds.`,
    )
  }
  if (feeResolution.disagreement) {
    const { quote, build } = feeResolution.disagreement
    throw new Error(
      'KyberSwap safety check failed: fee configuration changed between quote and build ' +
        `(quote: feeBps=${quote.feeBps} recipient=${quote.feeRecipient} quoteIsNetOfFee=${quote.quoteIsNetOfFee} chargeFeeBy=${quote.chargeFeeBy}; ` +
        `build: feeBps=${build.feeBps} recipient=${build.feeRecipient} quoteIsNetOfFee=${build.quoteIsNetOfFee} chargeFeeBy=${build.chargeFeeBy}) ` +
        '[FEE_META_DISAGREEMENT]. Transaction blocked to protect funds.',
    )
  }

  const minimumAmountOut = kyberMinimumAmountOut(
    quotedAmountOut,
    feeResolution.fee.quoteIsNetOfFee,
    feeResolution.fee.feeBps,
    slippageBps,
  )
  if (buildAmountOut < minimumAmountOut) {
    throw new Error(
      `KyberSwap safety check failed: fresh build amountOut (${buildAmountOut.toString()}) is below the enforced minimum (${minimumAmountOut.toString()}) [MINIMUM_OUT_BELOW_FLOOR]. Transaction blocked to protect funds.`,
    )
  }
}
